import { Check, Link2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TrackingEvents, type TrackingEventProps } from "../services/trackingEvents";
import { trackingService } from "../services/trackingService";
import { copyToClipboard } from "../utils/clipboard";

interface ProductShareButtonsProps {
  title: string;
  trackingProps?: TrackingEventProps;
  /** 44px squares by default; compact chart controls use 32px. */
  size?: "sm" | "md" | "lg";
}

const ProductShareButtons: React.FC<ProductShareButtonsProps> = ({ title, trackingProps = {}, size = "md" }) => {
  const buttonClassName = `grid place-items-center rounded-v7-control border border-v7-ink/20 bg-v7-white text-v7-ink-soft transition-colors hover:border-v7-bronze-dark hover:text-v7-bronze-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark ${
    size === "lg" ? "h-14 w-14" : size === "sm" ? "h-8 w-8" : "h-11 w-11"
  }`;
  const iconClassName = size === "lg" ? "h-[22px] w-[22px]" : "h-4 w-4";
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);
  const url = window.location.href;

  useEffect(() => () => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await copyToClipboard(url);
      trackingService.track(TrackingEvents.SHARE_LINK_COPY, trackingProps);
      setCopied(true);
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [trackingProps, url]);

  const handleShareOnX = useCallback(() => {
    const text = encodeURIComponent(`Check out "${title}" on Plutus`);
    const link = encodeURIComponent(url);
    window.open(`https://x.com/intent/tweet?text=${text}&url=${link}`, "_blank", "noopener");
    trackingService.track(TrackingEvents.SHARE_SOCIAL_CLICK, {
      ...trackingProps,
      network: "x",
    });
  }, [title, trackingProps, url]);

  return (
    <div className={`flex shrink-0 items-center ${size === "lg" ? "gap-2.5" : "gap-1.5"}`}>
      <button
        type="button"
        onClick={handleShareOnX}
        aria-label="Share on X"
        title="Share on X"
        className={buttonClassName}
      >
        <svg className={iconClassName} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied" : "Copy link"}
        className={buttonClassName}
      >
        {copied
          ? <Check className={`${iconClassName} text-v7-success`} aria-hidden />
          : <Link2 className={iconClassName} aria-hidden />
        }
      </button>
    </div>
  );
};

export default ProductShareButtons;
